import '../styles/Header.css';

import Feature, { FeatureKey } from '../../shared/models/Feature';
import React, { useEffect, useState } from 'react';
import { Link as RouterLink, useLocation, useNavigate } from 'react-router-dom';

import AuthService from '../../shared/services/AuthService';
import Drawer from '@mui/material/Drawer';
import Grid from '@mui/material/Unstable_Grid2';
import IconButton from '@mui/material/IconButton';
import Link from '@mui/material/Link';
import MenuIcon from '@mui/icons-material/Menu';
import Navigation from './Navigation';
import { Notice } from 'notifications/models/Notice';
import { NoticeReceiver } from 'notifications/models/NoticeReceiver';
import Paper from '@mui/material/Paper';
import { SkinTheme } from '../../design/models/SkinTheme';
import { SkinThemeConsumer } from '../../shared/SkinThemeContext';
import SkinThemeService from '../../shared/services/SkinThemeService';
import Typography from '@mui/material/Typography';
import User from '../../membership/models/User';
import useNotifications from '../../notifications/hooks/useNotifications';

interface HeaderProps {
  user: User;
  features: Feature[];
  onNotificationsUpdate: (
    notifications: NoticeReceiver[],
    sentNotifications: Notice[]
  ) => void;
}

const Header = (props: HeaderProps) => {
  const [menuDrawerOpen, setMenuDrawerOpen] = useState(false);
  //* Top level tabs only on first part of route, e.g. '/reporting/:report' -> 'reporting'.
  const path = /\/?(\w*)/.exec(useLocation().pathname)[1];
  const [selectedFeature, setSelectedFeature] = useState<FeatureKey>(
    (path as FeatureKey) || 'dashboard'
  );

  const location = useLocation();
  const navigate = useNavigate();

  const { receivedNotifications, sentNotifications } = useNotifications(
    props.user?.userkey
  );
  useEffect(() => {
    if (location.pathname.startsWith('/notifications')) {
      props.onNotificationsUpdate(receivedNotifications, sentNotifications);
    }
  }, [receivedNotifications, sentNotifications, location]);

  useEffect(() => {
    setSelectedFeature((path || 'dashboard') as FeatureKey);
  }, [location]);

  function getNumberOfUnreadNotifications(
    receivedNotifications: NoticeReceiver[]
  ) {
    return receivedNotifications?.filter((n) => !n.read).length || 0;
  }

  const numUnreadNotifications = getNumberOfUnreadNotifications(
    receivedNotifications
  );

  const logout = () => AuthService.logout();

  function handleMenuDrawerOpen() {
    setMenuDrawerOpen(true);
  }

  function handleMenuDrawerClose() {
    setMenuDrawerOpen(false);
  }

  function handleNavigate(feature: FeatureKey) {
    if (menuDrawerOpen) {
      setMenuDrawerOpen(false);
    }
    navigate(`/${feature}`);
    if (feature === 'notifications') {
      props.onNotificationsUpdate(receivedNotifications, sentNotifications);
    }
    setSelectedFeature(feature);
  }

  return (
    <SkinThemeConsumer>
      {(themeOptions: SkinTheme) => (
        <>
          <div
            className="Header-header"
            style={{
              color:
                SkinThemeService.getThemeOptionValue(
                  themeOptions,
                  'container',
                  'headerFontColor'
                ) || 'white',
              backgroundColor: SkinThemeService.getThemeOptionValue(
                themeOptions,
                'container',
                'headerColor'
              )
            }}
          >
            <Grid container wrap="nowrap">
              <Grid>
                <RouterLink to="/">
                  <span className="Header-logo">
                    <img
                      src={SkinThemeService.getAssetUrl(
                        'container',
                        'headerLogo'
                      )}
                      alt="Bracken"
                    />
                  </span>
                </RouterLink>
              </Grid>
              <Grid style={{ flex: 1 }}>
                <div className="header-menu">
                  <Navigation
                    orientation="horizontal"
                    features={props.features}
                    selectedFeature={selectedFeature}
                    numNotifications={numUnreadNotifications}
                    onNavigate={handleNavigate}
                  />
                </div>
              </Grid>
              <div className="logout-button">
                <Grid>
                  <Link
                    style={{ color: 'inherit', textDecoration: 'none' }}
                    onClick={logout}
                  >
                    <Typography variant="button">LOG OUT</Typography>
                  </Link>
                </Grid>
              </div>
              <div className="menu-button">
                <Grid>
                  <IconButton
                    color="inherit"
                    aria-label="open drawer"
                    onClick={handleMenuDrawerOpen}
                    edge="start"
                    // className={clsx(classes.menuButton, open && classes.hide)}
                  >
                    <MenuIcon />
                  </IconButton>
                </Grid>
              </div>
            </Grid>
          </div>
          <Drawer
            className="menu-drawer"
            variant="temporary"
            anchor="right"
            open={menuDrawerOpen}
            onClose={handleMenuDrawerClose}
          >
            <Paper
              style={{
                height: '100%',
                color:
                  SkinThemeService.getThemeOptionValue(
                    themeOptions,
                    'container',
                    'headerFontColor'
                  ) || 'white',
                backgroundColor: SkinThemeService.getThemeOptionValue(
                  themeOptions,
                  'container',
                  'headerColor'
                )
              }}
            >
              <Grid
                className="menu-tabs"
                container
                direction="column"
                alignItems="center"
                wrap="nowrap"
              >
                <Grid style={{ flex: 1 }}>
                  <Navigation
                    orientation="vertical"
                    features={props.features}
                    selectedFeature={selectedFeature}
                    numNotifications={numUnreadNotifications}
                    onNavigate={handleNavigate}
                  />
                </Grid>
                <Grid>
                  <div className="logout-button">
                    <Link
                      style={{ color: 'inherit', textDecoration: 'none' }}
                      onClick={logout}
                    >
                      <Typography align="center" variant="button">
                        LOG OUT
                      </Typography>
                    </Link>
                  </div>
                </Grid>
              </Grid>
            </Paper>
          </Drawer>
        </>
      )}
    </SkinThemeConsumer>
  );
};

export default Header;
